import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { SegmentedControl } from "@/components/design/SegmentedControl";
import { todayIso } from "@/lib/date-instant";
import { ProjectPlanList } from "../ProjectPlanList";
import { ProjectSwimlanes } from "../ProjectSwimlanes";
import { ProjectEditBox } from "../ProjectEditBox";
import { ProjectPackageFields, ProjectTaskFields } from "../ProjectTaskFields";
import type { PlanPerson, ProjectEvent, ProjectPackage, ProjectTask } from "../project-plan";

// Guided · Schritt 2/4. Der Plan selbst ist die Eingabemaske: oben steht die
// Darstellung, bearbeitet wird direkt darin. Das frühere Formular mit vier
// Feldern je Zeile ist ersatzlos weg - es zeigte dieselben Daten ein zweites
// Mal, nur unleserlicher.
//
// Zwei Sichten auf denselben Stand, wie in der Timeline: Projektfokus ordnet
// nach Zeit, Personfokus nach Mensch. Wer im Personfokus einen Balken anklickt,
// bekommt seine Felder direkt unter der Spur.

// Echte UUID, nicht Zeitstempel plus Zufall: diese Id ist der einzige Anker, an
// dem sich eine Aufgabe oder ein Termin über Umbenennungen und Umsortierungen
// hinweg wiedererkennen lässt. Sobald genesis darauf verweist - eine
// Benachrichtigung trägt ihre Bezugsgrösse als `Notification.entityId`, und das
// Feld ist ein UUID -, muss die Form stimmen. Jetzt kostet sie eine Zeile,
// später wäre es eine Datenwanderung.
const uid = () => globalThis.crypto.randomUUID();

type Focus = "project" | "person";

export function ProjectGuidedAufteilenScreen({
  packages,
  events,
  people,
  involved,
  lookupPerson,
  onChange,
  onEventsChange,
  readOnly = false,
}: {
  packages: readonly ProjectPackage[];
  /** Termine des Projekts: Zeitpunkte neben den Zeiträumen. */
  events: readonly ProjectEvent[];
  /** Zuweisbare Menschen (die eigenen Kontakte). */
  people: readonly PlanPerson[];
  /** Menschen mit mindestens einer Aufgabe - sie bekommen die Spuren im Personfokus. */
  involved: readonly PlanPerson[];
  lookupPerson: (id: string | null) => PlanPerson | null;
  onChange: (next: readonly ProjectPackage[]) => void;
  onEventsChange: (next: readonly ProjectEvent[]) => void;
  /** Nur lesen (`projectRights`): der Plan wird gezeigt, nicht bearbeitet. */
  readOnly?: boolean;
}) {
  const intl = useIntl();
  const [focus, setFocus] = useState<Focus>("project");
  // Eine Id für beides: angeklickt wird ein BALKEN, und ob der ein Paket oder
  // eine Aufgabe ist, entscheidet erst, welche Felder der Kasten zeigt. Zwei
  // getrennte Zustände hiessen, beim Klick den einen zu setzen und den anderen
  // zu vergessen.
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  // Wie in der Liste: wird die Seite nur lesbar, während ein Kasten offen ist, bleibt er zu.
  const openTaskId = readOnly ? null : selectedTaskId;

  const selected = packages
    .flatMap((pkg) => pkg.tasks.map((task) => ({ pkg, task })))
    .find((x) => x.task.id === openTaskId);
  const selectedPackage = packages.find((pkg) => pkg.id === openTaskId) ?? null;

  function patchPackage(id: string, patch: Partial<ProjectPackage>) {
    onChange(packages.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  }

  function patchTask(packageId: string, taskId: string, patch: Partial<ProjectTask>) {
    const pkg = packages.find((p) => p.id === packageId);
    if (!pkg) return;
    patchPackage(packageId, { tasks: pkg.tasks.map((t) => (t.id === taskId ? { ...t, ...patch } : t)) });
  }

  // Der Vorschlagsname ist ein ganz normaler Text, den man sofort überschreiben
  // kann. Ein neues Paket beginnt, wo das letzte endet - das ist die Annahme, die
  // beim Planen am seltensten korrigiert werden muss.
  // Die Anlege-Funktionen geben die neue Id zurück, damit die Liste die Zeile
  // gleich zum Bearbeiten aufklappen kann. Wer etwas hinzufügt, will es benennen,
  // nicht erst suchen und anklicken.
  function addPackage() {
    const last = packages[packages.length - 1];
    const start = last?.end || todayIso();
    const id = uid();
    onChange([
      ...packages,
      {
        id,
        // "Paket", nicht "Abschnitt": so heisst das Ding in jedem Knopf, jeder
        // Überschrift und jeder Löschfrage. Der Vorgabename stammte noch aus der
        // Zeit davor und war das einzige Wort, das widersprach.
        name: intl.formatMessage(
          { id: "project.guided.aufteilen.newSectionName", defaultMessage: "Aufgabenpaket {n}" },
          { n: packages.length + 1 },
        ),
        assigneeId: null,
        start,
        end: start,
        tasks: [],
      },
    ]);
    return id;
  }

  // Ein neuer Termin liegt auf heute: das ist der einzige Tag, von dem man
  // sicher weiss, dass der Mensch ihn gerade im Kopf hat.
  function addEvent() {
    const id = uid();
    onEventsChange([
      ...events,
      {
        id,
        title: intl.formatMessage({ id: "project.plan.newEventName", defaultMessage: "Neuer Termin" }),
        date: todayIso(),
        kind: "milestone",
      },
    ]);
    return id;
  }

  function addTask(packageId: string): string | null {
    const pkg = packages.find((p) => p.id === packageId);
    if (!pkg) return null;
    const task: ProjectTask = {
      id: uid(),
      title: intl.formatMessage({
        id: "project.guided.aufteilen.newTaskName",
        defaultMessage: "Neue Aufgabe",
      }),
      assigneeId: null,
      start: pkg.start,
      end: pkg.end,
    };
    patchPackage(packageId, { tasks: [...pkg.tasks, task] });
    setSelectedTaskId(task.id);
    return task.id;
  }

  return (
    <div>
      <div>
        <p className="smycles-field-meta max-w-[62ch] text-ink">
          <FormattedMessage
            id="project.guided.aufteilen.sub"
            defaultMessage="Aufgabenpakete mit Zeitraum, darin die Aufgaben, dazwischen die Termine. Vier bis acht Pakete reichen."
          />
        </p>
        {/* Links, auf derselben senkrechten Kante wie Einleitung und Zeilen: die
            Umschalter gehören zum Inhalt darunter, nicht in eine Werkzeugecke am
            rechten Rand, wo sie mit nichts mehr auf einer Linie stehen.
            Ohne size-Angabe, also `md` wie überall sonst: derselbe Umschalter
            steht auf der Dokumentenseite ("Vorschau / Kompakt") und in der
            Pinnwand in dieser Grösse. Mit `sm` wirkte ausgerechnet die
            Hauptumschaltung dieser Fläche kleiner als jede andere im Produkt. */}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <SegmentedControl
            id="project-plan-focus"
            value={focus}
            onChange={setFocus}
            options={[
              {
                value: "project",
                label: intl.formatMessage({ id: "project.timeline.focusProject", defaultMessage: "Projektfokus" }),
              },
              {
                value: "person",
                label: intl.formatMessage({ id: "project.timeline.focusPerson", defaultMessage: "Personfokus" }),
              },
            ]}
          />
        </div>
      </div>

      <div className="mt-5">
        {focus === "person" ? (
          <>
            <ProjectSwimlanes
              packages={packages}
              events={events}
              people={involved}
              selectedTaskId={openTaskId}
              onSelectTask={readOnly ? undefined : (id) => setSelectedTaskId((cur) => (cur === id ? null : id))}
            />
            <div className="mt-4 max-w-2xl">
              {selected ? (
                <ProjectEditBox
                  idPrefix={`project-lane-task-${selected.task.id}`}
                  onDone={() => setSelectedTaskId(null)}
                  onDelete={() => {
                    patchPackage(selected.pkg.id, {
                      tasks: selected.pkg.tasks.filter((t) => t.id !== selected.task.id),
                    });
                    setSelectedTaskId(null);
                  }}
                  deleteLabel={intl.formatMessage({
                    id: "project.plan.removeTask",
                    defaultMessage: "Aufgabe löschen",
                  })}
                  confirmQuestion={intl.formatMessage({
                    id: "project.plan.removeTaskConfirm",
                    defaultMessage: "Aufgabe löschen?",
                  })}
                >
                  <ProjectTaskFields
                    task={selected.task}
                    people={people}
                    assignee={lookupPerson(selected.task.assigneeId)}
                    onChange={(patch) => patchTask(selected.pkg.id, selected.task.id, patch)}
                  />
                </ProjectEditBox>
              ) : selectedPackage ? (
                <ProjectEditBox
                  idPrefix={`project-lane-package-${selectedPackage.id}`}
                  onDone={() => setSelectedTaskId(null)}
                  onDelete={() => {
                    onChange(packages.filter((p) => p.id !== selectedPackage.id));
                    setSelectedTaskId(null);
                  }}
                  deleteLabel={intl.formatMessage({
                    id: "project.plan.deletePackage",
                    defaultMessage: "Aufgabenpaket löschen",
                  })}
                  confirmQuestion={intl.formatMessage(
                    {
                      id: "project.plan.deletePackageConfirm",
                      defaultMessage:
                        "{count, plural, =0 {Aufgabenpaket löschen?} one {Mit einer Aufgabe löschen?} other {Mit # Aufgaben löschen?}}",
                    },
                    { count: selectedPackage.tasks.length },
                  )}
                >
                  <ProjectPackageFields
                    pkg={selectedPackage}
                    people={people}
                    assignee={lookupPerson(selectedPackage.assigneeId)}
                    onChange={(patch) => patchPackage(selectedPackage.id, patch)}
                    onAddTask={() => addTask(selectedPackage.id)}
                  />
                </ProjectEditBox>
              ) : readOnly ? null : (
                <p className="box-flat bg-page p-3 smycles-field-meta text-ink-muted">
                  <FormattedMessage
                    id="project.plan.selectHint"
                    defaultMessage="Klick einen Balken an, um ihn hier zu ändern."
                  />
                </p>
              )}
            </div>
          </>
        ) : (
          <ProjectPlanList
            packages={packages}
            events={events}
            people={people}
            lookupPerson={lookupPerson}
            onChange={onChange}
            onEventsChange={onEventsChange}
            onAddTask={addTask}
            onAddPackage={addPackage}
            onAddEvent={addEvent}
            readOnly={readOnly}
          />
        )}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        {/* Im Projektfokus hängt "+ Paket" am Ende der Liste, wo es hingehört;
            der Personfokus hat keine solche Stelle und bekommt den Knopf hier. */}
        {focus === "person" && !readOnly ? (
          <>
            <Button variant="secondary" size="compact" onClick={addPackage}>
              <FormattedMessage id="project.guided.aufteilen.addSection" defaultMessage="+ Aufgabenpaket" />
            </Button>
            <Button variant="secondary" size="compact" onClick={addEvent}>
              <FormattedMessage id="project.guided.aufteilen.addEvent" defaultMessage="+ Termin" />
            </Button>
          </>
        ) : null}
      </div>

      {/* Kein "Weiter" mehr: das war der Knopf des Assistenten, der von Schritt zu
          Schritt führte. In einem Reiter führt er nirgendwohin, und gespeichert
          ist ohnehin alles. */}
      {readOnly ? null : (
        <div className="mt-5">
          <span className="smycles-field-meta text-ink-muted">
            <FormattedMessage id="project.guided.aufteilen.autosaved" defaultMessage="Automatisch gespeichert" />
          </span>
        </div>
      )}
    </div>
  );
}
